import * as React from "react";
import { BarChart as BarChartRe, Bar, AreaChart as AreaChartRe, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList, ResponsiveContainer } from 'recharts';
import ChartTooltip from "./ChartTooltip";
import getColor from "./colors";
import { formatNumber } from "../../helpers/UtilityHelper";
import ChartEmpty from "./ChartEmpty";

// Value label rendered on/next to each bar. Opt-in via chartOptions.showLabels so
// dense series (e.g. daily trends) elsewhere stay clean. position: 'top' for vertical
// bars, 'right' for horizontal (layout="vertical") bars.
const valueLabelStyle = { fontSize: 11, fontWeight: 600, fill: '#454F5B' };
// Bolder style for a single per-column total sitting on top of a stack.
const totalLabelStyle = { fontSize: 11, fontWeight: 800, fill: '#2b3648' };
// White, centered value inside a stacked segment.
const segLabelStyle = { fontSize: 10, fontWeight: 700, fill: '#ffffff' };
const formatBarValue = (v) => (v === 0 || v ? formatNumber(v) : '');

export function BarChart({ width = "100%", data, height, chartOptions, ...other }) {
    if (!data || data.length === 0) return <ChartEmpty height={height || 350} />

    return (
        <ResponsiveContainer width={width} height={height ? height : 350}>
            <BarChartRe
                data={data}
                {...other}
            >
                <CartesianGrid strokeDasharray="5 5" stroke="#dee2e6" />
                <XAxis dataKey={chartOptions.xaxis} label={{ value: (chartOptions.xLabel ? chartOptions.xLabel : ''), position: 'insideBottom', offset: 0, dy: 3, fontSize: '11px', fontWeight: 500, fill: '#666666' }} fontSize="11px" fontWeight="500" />
                <YAxis label={{ value: (chartOptions.yLabel ? chartOptions.yLabel : ''), position: "insideLeft", angle: -90, dy: -10, fontSize: '11px', fontWeight: 500, fill: '#666666' }} fontSize="11px" fontWeight="500" />
                <Tooltip content={<ChartTooltip />} />
                <Legend verticalAlign="top" align="right" iconSize="10px" iconType="circle" wrapperStyle={{ fontSize: 11, fontWeight: 500 }} />
                {
                    chartOptions.data.map((row, i) => (
                        <Bar
                            key={i}
                            dataKey={row.dataKey}
                            fill={row.fill || getColor(row.colorIndex || i)}
                            name={row.label}
                            maxBarSize={40}
                            unit={row.unit || ""}
                            animationEasing="ease-in-out"
                            animationDuration={1000}
                        >
                            {chartOptions.showLabels && (
                                <LabelList dataKey={row.dataKey} position="top" style={valueLabelStyle} formatter={formatBarValue} />
                            )}
                        </Bar>
                    ))
                }
            </BarChartRe>
        </ResponsiveContainer>
    )
}

export function VerticalBarChart({ data, height, width, chartOptions, ...other }) {
    if (!data || data.length === 0) return <ChartEmpty height={height || 350} />

    return (
        <ResponsiveContainer width={width || '100%'} height={height ? height : 350}>
            <BarChartRe
                data={data}
                {...other}
                layout="vertical"
            >
                <CartesianGrid strokeDasharray="5 5" stroke="#dee2e6" />
                <XAxis dataKey={chartOptions.xaxis} label={{ value: (chartOptions.xLabel ? chartOptions.xLabel : ''), position: 'insideBottom', offset: 0, dy: 3, fontSize: '11px', fontWeight: 500, fill: '#666666' }} type="number" fontSize="11px" fontWeight="500" />
                <YAxis label={{ value: (chartOptions.yLabel ? chartOptions.yLabel : ''), position: "insideLeft", angle: -90, dy: 0, fontSize: '11px', fontWeight: 500, fill: '#666666' }} dataKey={chartOptions.yaxis} reversed type="category" fontSize="11px" fontWeight="500" />
                <Tooltip content={<ChartTooltip />} />
                <Legend verticalAlign="top" align="right" iconSize="10px" iconType="circle" wrapperStyle={{ fontSize: 11, fontWeight: 500 }} />
                {
                    chartOptions.data.map((row, i) => (
                        <Bar
                            key={i}
                            dataKey={row.dataKey}
                            fill={row.fill || getColor(row.colorIndex || i)}
                            name={row.label}
                            unit={row.unit || ""}
                            maxBarSize={40}
                            animationEasing="ease-in-out"
                            animationDuration={1000}
                        >
                            {chartOptions.showLabels && (
                                <LabelList dataKey={row.dataKey} position="right" style={valueLabelStyle} formatter={formatBarValue} />
                            )}
                        </Bar>
                    ))
                }
            </BarChartRe>
        </ResponsiveContainer>
    )
}

export function StackedBarChart({ data, height, chartOptions, ...other }) {
    if (!data || data.length === 0) return <ChartEmpty height={height || 350} />
    return (
        <ResponsiveContainer width="100%" height={height ? height : 350}>
            <BarChartRe
                data={data}
                {...other}
            >
                <CartesianGrid strokeDasharray="5 5" stroke="#dee2e6" />
                <XAxis dataKey={chartOptions.xaxis} fontSize="11px" fontWeight="500" label={{ value: (chartOptions.xLabel ? chartOptions.xLabel : ''), position: 'insideBottom', offset: 0, dy: 3, fontSize: '11px', fontWeight: 500, fill: '#666666' }} />
                <YAxis fontSize="11px" fontWeight="500" label={{ value: chartOptions.labelY, angle: -90, position: 'insideLeft', fontSize: '11px', fontWeight: 500, fill: '#666666' }} />
                <Tooltip content={<ChartTooltip />} />
                <Legend verticalAlign="top" align="right" iconSize="10px" iconType="circle" wrapperStyle={{ fontSize: 11, fontWeight: 500 }} />
                {
                    chartOptions.data.map((row, i) => {
                        const isTop = i === chartOptions.data.length - 1
                        // Hide labels for segments too small to hold text — prevents overlap.
                        const segFmt = (v) => (v && v >= (chartOptions.labelMin || 0) ? formatNumber(v) : '')
                        return (<Bar
                            key={i}
                            dataKey={row.dataKey}
                            fill={row.fill || getColor(row.colorIndex || i)}
                            name={row.label}
                            stackId={chartOptions.stackId}
                            maxBarSize={46}
                            unit={row.unit || ""}
                            animationEasing="ease-in-out"
                            animationDuration={1000}
                        >
                            {chartOptions.showLabels && (
                                <LabelList dataKey={row.dataKey} position="center" formatter={segFmt} style={segLabelStyle} />
                            )}
                            {chartOptions.totalKey && isTop && (
                                <LabelList dataKey={chartOptions.totalKey} position="top" offset={8} style={totalLabelStyle} formatter={formatBarValue} />
                            )}
                        </Bar>)
                    })
                }
            </BarChartRe>
        </ResponsiveContainer>
    )
}
// Stacked AREA variant of StackedBarChart — same chartOptions API (xaxis, stackId, data,
// labelY). Reads status/volume over time as a flow rather than a wall of columns.
export function StackedAreaChart({ data, height, chartOptions, ...other }) {
    if (!data || data.length === 0) return <ChartEmpty height={height || 350} />
    return (
        <ResponsiveContainer width="100%" height={height ? height : 350}>
            <AreaChartRe
                data={data}
                {...other}
            >
                <CartesianGrid strokeDasharray="5 5" stroke="#dee2e6" />
                <XAxis dataKey={chartOptions.xaxis} fontSize="11px" fontWeight="500" label={{ value: (chartOptions.xLabel ? chartOptions.xLabel : ''), position: 'insideBottom', offset: 0, dy: 3, fontSize: '11px', fontWeight: 500, fill: '#666666' }} />
                <YAxis fontSize="11px" fontWeight="500" label={{ value: chartOptions.labelY, angle: -90, position: 'insideLeft', fontSize: '11px', fontWeight: 500, fill: '#666666' }} />
                <Tooltip content={<ChartTooltip />} />
                <Legend verticalAlign="top" align="right" iconSize="10px" iconType="circle" wrapperStyle={{ fontSize: 11, fontWeight: 500 }} />
                {
                    chartOptions.data.map((row, i) => {
                        const c = row.fill || getColor(row.colorIndex || i)
                        const isTop = i === chartOptions.data.length - 1
                        return (<Area
                            key={i}
                            type="monotone"
                            dataKey={row.dataKey}
                            name={row.label}
                            stackId={chartOptions.stackId || 'stack'}
                            fill={c}
                            stroke={c}
                            fillOpacity={0.5}
                            unit={row.unit || ""}
                            animationEasing="ease-in-out"
                            animationDuration={1000}
                        >
                            {chartOptions.showLabels && (
                                <LabelList dataKey={row.dataKey} position="top" style={valueLabelStyle} formatter={formatBarValue} />
                            )}
                            {/* one clean total per column, drawn once on the topmost band */}
                            {chartOptions.totalKey && isTop && (
                                <LabelList dataKey={chartOptions.totalKey} position="top" offset={8} style={totalLabelStyle} formatter={formatBarValue} />
                            )}
                        </Area>)
                    })
                }
            </AreaChartRe>
        </ResponsiveContainer>
    )
}
export function HorizontalStackedBarChart({ data, height, chartOptions, ...other }) {
    return (
        <ResponsiveContainer height={23} width={'100%'}>
            <BarChart
                data={data}
                layout={'vertical'}
                barGap={10}
                margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
            >
                <XAxis type={'number'} hide domain={['dataMin', 'dataMax']} />
                <YAxis
                    dataKey='axis'
                    type={'category'}
                    axisLine={false}
                    tickLine={false}
                    hide
                />
                {data.map((key, i) => (
                    <Bar key={key} stackId={'stack'} dataKey={key} fill={key.fill || getColor(key.colorIndex || i)} />
                ))}
            </BarChart>
        </ResponsiveContainer>
    )
}